import { useState } from "react";
import { useNavigate } from "react-router";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { startOfDay, startOfToday, subDays } from "date-fns";
import { z } from "zod";
import { Trans, useLingui } from "@lingui/react/macro";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FilledButton } from "@medoma/components/Button/Button";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { Heading } from "@medoma/components/Heading/Heading";
import InputField from "@medoma/components/InputField/InputField";
import { Loading } from "@medoma/components/Loading/Loading";
import { Text } from "@medoma/components/Text/Text";
import RadioButton from "@medoma/components/RadioButton/RadioButton";
import RadioButtons from "@medoma/components/RadioButton/RadioButtons";
import { dateInputSchema } from "@medoma/models/date-and-time";
import {
  patientStatusSchema,
  type IAdmittedPatient,
  type IExistingPatient,
} from "@medoma/models/patients";
import Form from "@/components/Form/Form";
import {
  dischargePatient,
  useParentRoutesPatient,
  type IDischargePatient,
} from "@/api/Patients";
import { useActivitiesToCloseForPatient } from "@/api/Activities";
import { deducedError } from "@/Utils/ErrorUtils";
import {
  ActivitiesToClose,
  type IActivitiesToCloseFields,
} from "../../ActivitiesToClose/ActivitiesToClose";
import { DischargeReview } from "./DischargeReview";
import styles from "./DischargePatient.module.scss";

const timeOfDischargeSchema = z.enum(["today", "earlier"]);

type IDischargeFormData = IActivitiesToCloseFields & {
  timeOfDischarge: z.infer<typeof timeOfDischargeSchema>;
  /** The date input's value, only used when discharging on an earlier date */
  dischargeDate: z.input<typeof dateInputSchema>;
};

const DischargePatientForm = ({ patient }: { patient: IAdmittedPatient }) => {
  const { t } = useLingui();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    data: activities = [],
    isPending: isFetchingActivities,
    isError: couldNotFetchActivities,
  } = useActivitiesToCloseForPatient(patient.id);

  const methods = useForm<IDischargeFormData>({
    // Recurring activities are preselected once they have been fetched;
    // one-time activities are left unchecked so the user opts in explicitly
    values: {
      activityIds: activities
        .filter(({ recurring }) => recurring)
        .map(({ id }) => id),
      activitiesAreClosed: false,
      timeOfDischarge: timeOfDischargeSchema.enum.today,
      dischargeDate: "",
    },
    resetOptions: { keepDirtyValues: true },
  });
  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    resetField,
  } = methods;
  const timeOfDischarge = useWatch({ control, name: "timeOfDischarge" });

  const earliestDischargeDate = startOfDay(patient.admittedAt);
  const latestEarlierDischargeDate = subDays(startOfToday(), 1);
  // A patient admitted today has no date before today to be discharged on
  const canDischargeOnEarlierDate =
    earliestDischargeDate <= latestEarlierDischargeDate;

  const [dischargeToReview, setDischargeToReview] =
    useState<IDischargePatient | null>(null);

  const {
    mutate: dischargePatientMutation,
    isPending: isDischarging,
    isError: couldNotDischarge,
    error,
    reset: resetDischarge,
  } = useMutation({
    mutationFn: (discharge: IDischargePatient) =>
      dischargePatient({ patientId: patient.id, discharge }),
    onSuccess: async () => {
      // A discharged patient can't be worked with in Go, so the discharge
      // continues to the patient list. Pages that aren't shown may have cached
      // the patient as admitted, so they are fetched anew instead of shown as
      // before
      queryClient.removeQueries({ type: "inactive" });
      await navigate("/patients", { replace: true });
      await queryClient.invalidateQueries();
    },
  });

  const validateAndReview = handleSubmit(
    ({ activityIds, timeOfDischarge, dischargeDate }) =>
      setDischargeToReview({
        dischargedAt:
          timeOfDischarge === timeOfDischargeSchema.enum.earlier
            ? (dateInputSchema.decode(dischargeDate) ?? undefined)
            : undefined,
        activitiesToClose: activityIds,
      }),
  );

  if (isFetchingActivities) {
    return <Loading message={t`Hämtar information om aktiviteter`} />;
  }

  const timeOfDischargeField = register("timeOfDischarge", {
    onChange: () => resetField("dischargeDate"),
  });

  return (
    <FormProvider {...methods}>
      <Form onSubmit={validateAndReview}>
        <RadioButtons legend={t`Utskrivningsdatum`} orientation="horizontal">
          <RadioButton
            label={{ text: t`Idag` }}
            {...timeOfDischargeField}
            value={timeOfDischargeSchema.enum.today}
            visualStyle="framed"
          />
          <RadioButton
            label={{ text: t`Tidigare datum` }}
            {...timeOfDischargeField}
            value={timeOfDischargeSchema.enum.earlier}
            disabled={!canDischargeOnEarlierDate}
            visualStyle="framed"
          />
        </RadioButtons>
        {timeOfDischarge === timeOfDischargeSchema.enum.earlier ? (
          <InputField
            label={t`Datum`}
            type="date"
            {...register("dischargeDate", {
              required: t`Välj ett datum`,
              validate: (value) => {
                const dischargeDate = dateInputSchema.decode(value);
                if (dischargeDate === null) {
                  return true;
                }
                if (dischargeDate > latestEarlierDischargeDate) {
                  return t`Välj ett datum före idag`;
                }
                if (dischargeDate < earliestDischargeDate) {
                  return t`Utskrivningsdatumet kan inte vara före inskrivningsdatumet`;
                }
                return true;
              },
            })}
            errorMessage={errors.dischargeDate?.message}
            showOptionalLabel={false}
          />
        ) : null}
        <ActivitiesToClose
          activities={activities}
          couldNotFetchActivities={couldNotFetchActivities}
          instructions={t`Välj de aktiviteter som ska avslutas vid utskrivning`}
        />
        {activities.length > 0 ? (
          <Text element="p">
            <Trans>
              Alla aktiviteter som avslutas här kan återskapas vid
              återregistrering.
            </Trans>
          </Text>
        ) : null}
        <FilledButton type="submit">
          <Trans>Granska utskrivning</Trans>
        </FilledButton>
      </Form>
      {dischargeToReview ? (
        <DischargeReview
          patient={patient}
          dischargeDate={dischargeToReview.dischargedAt ?? new Date()}
          activitiesToCloseCount={dischargeToReview.activitiesToClose.length}
          activitiesToKeepCount={
            activities.length - dischargeToReview.activitiesToClose.length
          }
          isDischarging={isDischarging}
          errorMessage={
            couldNotDischarge
              ? `${t`Det gick inte att skriva ut patienten.`} ${deducedError(error)}`
              : null
          }
          onCancel={() => {
            // Closing the review can't stop a discharge that is already sent
            if (isDischarging) {
              return;
            }
            resetDischarge();
            setDischargeToReview(null);
          }}
          onDischarge={() => dischargePatientMutation(dischargeToReview)}
        />
      ) : null}
    </FormProvider>
  );
};

export const DischargePatient = ({
  patient,
}: {
  patient: IExistingPatient;
}) => {
  const { t } = useLingui();

  return (
    <section className={styles.dischargePatient}>
      <Heading level="h2">
        <Trans>Skriv ut</Trans>
      </Heading>
      {patient.status === patientStatusSchema.enum.admitted ? (
        <DischargePatientForm patient={patient} />
      ) : (
        <ErrorMessage
          message={t`Patienten är inte inskriven och kan därför inte skrivas ut.`}
        />
      )}
    </section>
  );
};

export const DischargeParentRoutesPatient = () => (
  <DischargePatient patient={useParentRoutesPatient()} />
);
